import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '../context/UIContext';
import { weekdayName } from '../utils/dateConverter';
import { PRIORITIES, PRIORITY_LABEL, STATUSES, STATUS_LABEL } from '../utils/planning';

const EMPTY = {
    title: '',
    description: '',
    external_link: '',
    estimate_hours: '',
    status: 'todo',
    assignee_id: '',
    planned_start: '',
    planned_end: '',
    priority: 'Medium',
    depends_on_id: '',
    is_milestone: false,
};

const fromTask = (task) => ({
    title: task.title || '',
    description: task.description || '',
    external_link: task.external_link || '',
    estimate_hours: task.estimate_hours ? String(task.estimate_hours) : '',
    status: task.status || 'todo',
    assignee_id: task.assignee_id ? String(task.assignee_id) : '',
    planned_start: task.planned_start || '',
    planned_end: task.planned_end || '',
    priority: task.priority || 'Medium',
    depends_on_id: task.depends_on_id ? String(task.depends_on_id) : '',
    is_milestone: !!task.is_milestone,
});

/** Create or edit one task. An emptied field is sent as null, not omitted: the backend resolves
 *  the update from `model_fields_set`, where an omitted key means "leave it alone". */
export default function TaskModal({
    task = null, people = [], tasks = [], onClose, onSaved, onDelete = null,
}) {
    const toast = useToast();
    const [form, setForm] = useState(() => (task ? fromTask(task) : { ...EMPTY }));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setForm(task ? fromTask(task) : { ...EMPTY });
    }, [task]);

    const setField = (name) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    // The backend rejects a cycle, so one is not offered here.
    const possiblePredecessors = useMemo(
        () => (tasks || []).filter(t => String(t.id) !== String(task?.id)),
        [tasks, task],
    );

    const endBeforeStart = !!form.planned_start && !!form.planned_end
        && form.planned_end < form.planned_start;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.title.trim()) {
            toast('Bitte einen Titel angeben.');
            return;
        }
        if (endBeforeStart) {
            toast('Das Ende darf nicht vor dem Start liegen.');
            return;
        }
        const payload = {
            title: form.title.trim(),
            description: form.description.trim() || null,
            external_link: form.external_link.trim() || null,
            estimate_hours: parseFloat(form.estimate_hours) || 0,
            status: form.status,
            assignee_id: form.assignee_id || null,
            planned_start: form.planned_start || null,
            planned_end: form.planned_end || null,
            priority: form.priority,
            depends_on_id: form.depends_on_id || null,
            is_milestone: form.is_milestone,
        };
        setSaving(true);
        try {
            await onSaved(payload);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="pl-modal-backdrop" onClick={onClose}>
            <div className="pl-modal" onClick={e => e.stopPropagation()}>
                <header className="pl-modal-head">
                    <h3>{task ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}</h3>
                    <button type="button" className="pl-modal-close" onClick={onClose} aria-label="Schließen">
                        ×
                    </button>
                </header>

                <form onSubmit={handleSubmit} className="pl-form">
                    <label className="pl-field pl-field--full">
                        Titel *
                        <input
                            className="pl-input"
                            value={form.title}
                            onChange={setField('title')}
                            placeholder="z.B. Schnittstelle SAP → MES"
                            autoFocus
                        />
                    </label>

                    <label className="pl-field pl-field--full">
                        Beschreibung
                        <textarea
                            className="pl-textarea"
                            rows={3}
                            value={form.description}
                            onChange={setField('description')}
                        />
                    </label>

                    <label className="pl-field">
                        Zuständig
                        <select className="pl-input" value={form.assignee_id} onChange={setField('assignee_id')}>
                            <option value="">— niemand —</option>
                            {people.map(person => (
                                <option key={person.id} value={person.id}>
                                    {person.name || person.username}
                                </option>
                            ))}
                        </select>
                    </label>

                    <label className="pl-field">
                        Aufwand (h)
                        <input
                            className="pl-input"
                            type="number"
                            min="0"
                            step="0.25"
                            value={form.estimate_hours}
                            onChange={setField('estimate_hours')}
                            placeholder="z.B. 16"
                        />
                    </label>

                    <label className="pl-field">
                        Status
                        <select className="pl-input" value={form.status} onChange={setField('status')}>
                            {STATUSES.map(s => (
                                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                            ))}
                        </select>
                    </label>

                    <label className="pl-field">
                        Priorität
                        <select className="pl-input" value={form.priority} onChange={setField('priority')}>
                            {PRIORITIES.map(p => (
                                <option key={p} value={p}>{PRIORITY_LABEL[p]}</option>
                            ))}
                        </select>
                    </label>

                    <label className="pl-field">
                        Start{form.planned_start ? ` · ${weekdayName(form.planned_start)}` : ''}
                        <input
                            className="pl-input"
                            type="date"
                            value={form.planned_start}
                            onChange={setField('planned_start')}
                        />
                    </label>

                    <label className="pl-field">
                        Ende{form.planned_end ? ` · ${weekdayName(form.planned_end)}` : ''}
                        <input
                            className="pl-input"
                            type="date"
                            value={form.planned_end}
                            min={form.planned_start || undefined}
                            onChange={setField('planned_end')}
                        />
                    </label>

                    <label className="pl-field pl-field--full">
                        Startet nach
                        <select
                            className="pl-input"
                            value={form.depends_on_id}
                            onChange={setField('depends_on_id')}
                        >
                            <option value="">— keine Abhängigkeit —</option>
                            {possiblePredecessors.map(t => (
                                <option key={t.id} value={t.id}>{t.title || 'Ohne Titel'}</option>
                            ))}
                        </select>
                    </label>

                    <label className="pl-field pl-field--full">
                        Link (Jira, Ticket, …)
                        <input
                            className="pl-input"
                            value={form.external_link}
                            onChange={setField('external_link')}
                            placeholder="https://…"
                        />
                    </label>

                    <label className="pl-check pl-field--full">
                        <input
                            type="checkbox"
                            checked={form.is_milestone}
                            onChange={setField('is_milestone')}
                        />
                        Meilenstein
                    </label>

                    {endBeforeStart && (
                        <p className="pl-form-error pl-field--full">
                            Das Ende liegt vor dem Start.
                        </p>
                    )}

                    <footer className="pl-modal-foot pl-field--full">
                        {onDelete && (
                            <button
                                type="button"
                                className="pl-btn pl-btn--danger pl-btn--left"
                                onClick={onDelete}
                            >
                                Löschen
                            </button>
                        )}
                        <button type="button" className="pl-btn" onClick={onClose}>
                            Abbrechen
                        </button>
                        <button type="submit" className="pl-btn pl-btn--primary" disabled={saving}>
                            {task ? 'Änderungen speichern' : 'Aufgabe anlegen'}
                        </button>
                    </footer>
                </form>
            </div>
        </div>
    );
}
